import { memo, useCallback } from "react";
import { QueryStatus } from "@tanstack/react-query";
import { pluralizeWithCount } from "@common/helpers/string";
import { DocumentAccrualMatchSummary } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { useOverlays } from "contexts/OverlayContext";
import { MatchCountChip } from "./MatchCountChip";
import { MAX_VISIBLE_MATCH_ENTITY_ICONS } from "./matchCell";
import matchCellStyles from "./MatchCell.module.scss";
import styles from "./DocumentMatchCellContent.module.scss";

export interface DocumentMatchCellContentProps {
    documentType: "bill" | "invoice";
    documentId: string;
    status?: QueryStatus;
    summary?: DocumentAccrualMatchSummary;
    readonly?: boolean;
}

export const DocumentMatchCellContent = memo(
    ({
        documentType,
        documentId,
        status,
        summary,
        readonly,
    }: DocumentMatchCellContentProps) => {
        const { openOverlay } = useOverlays();

        const handleOpenMatchDrawer = useCallback(() => {
            openOverlay("documentAccrualMatch", { documentType, documentId });
        }, [documentType, documentId, openOverlay]);

        if (status === "pending") {
            return <ViewTableCell />;
        }

        if (status === "error") {
            return (
                <ViewTableCell captureClicks className={matchCellStyles.cell}>
                    <EditableCellContent
                        onClick={handleOpenMatchDrawer}
                        readonly={readonly}
                    >
                        <span className={styles.error}>
                            Failed to load matches
                        </span>
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        const matchCount = summary?.matchCount ?? 0;

        return (
            <ViewTableCell captureClicks className={matchCellStyles.cell}>
                <EditableCellContent
                    className={
                        matchCount > 0 ? matchCellStyles.match : undefined
                    }
                    isEmpty={matchCount === 0}
                    onClick={handleOpenMatchDrawer}
                    readonly={readonly}
                >
                    {summary && matchCount > 0 && (
                        <>
                            <EntityAvatarGroup
                                entities={summary.entities}
                                max={MAX_VISIBLE_MATCH_ENTITY_ICONS}
                            />
                            <span className={matchCellStyles.countChip}>
                                <MatchCountChip
                                    label={pluralizeWithCount(
                                        "transaction",
                                        matchCount,
                                    )}
                                />
                            </span>
                        </>
                    )}
                </EditableCellContent>
            </ViewTableCell>
        );
    },
);
DocumentMatchCellContent.displayName = "DocumentMatchCellContent";
